import React, { createContext, useState, ReactNode, useCallback, useEffect } from 'react';
import { Alert, Snackbar } from '@mui/material';
import { ErrorOutline as ErrorIcon } from '@mui/icons-material';
import { generateUniqueId } from '../helpers';
import { toastService } from '../services/toastService';

interface ToastMessage {
  id: string;
  message: string;
  type: 'success' | 'error' | 'custom';
  duration?: number;
  customColor?: string;
  customIcon?: React.ReactNode | false;
}

interface ToastContextType {
  showToast: (message: string, type: 'success' | 'error' | 'custom', duration?: number, customColor?: string, customIcon?: React.ReactNode | false) => void;
}

interface ToastProviderProps {
  children: ReactNode;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const styles = {
  snackbar: {
    bottom: (index: number) => `${20 + (index * 70)}px !important`
  },
  alert: {
    colors: {
      error: '#d32f2f',
      success: '#000000'
    },
    base: {
      minWidth: '300px',
      color: 'white'
    },
    withCustomColor: (customColor?: string) => ({
      backgroundColor: customColor || undefined
    })
  }
};

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  
  // Store timer IDs for cleanup on unmount
  const timerIds = React.useRef<NodeJS.Timeout[]>([]);

    const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const showToast = useCallback((
    message: string, 
    type: 'success' | 'error' | 'custom',
    duration: number = 45000,
    customColor?: string,
    customIcon?: React.ReactNode | false
  ) => {
    // Validate message
    if (!message || !message.trim()) {
      console.warn('Toast message cannot be empty');
      return;
    }

    const id = generateUniqueId();
  
    // Apply default styling based on type if not provided
    let finalColor = customColor;
    let finalIcon = customIcon;
    
    if (type === 'success' && !customColor) {
      finalColor = styles.alert.colors.success;
    } else if (type === 'error' && !customColor) {
      finalColor = styles.alert.colors.error;
    }
    
    if (type === 'success' && customIcon === undefined) {
      finalIcon = false; // No icon for success
    } else if (type === 'error' && customIcon === undefined) {
      finalIcon = <ErrorIcon />; // Error icon for errors
    }

    const newToast: ToastMessage = {
      id,
      message,
      type,
      duration,
      customColor: finalColor,
      customIcon: finalIcon
    };

    // Add toast to state
    setToasts(prev => [...prev, newToast]);

    // Auto remove after duration and store timer ID for cleanup
    const timerId = setTimeout(() => {
      removeToast(id);
    }, duration);
    timerIds.current.push(timerId);
  }, [removeToast]);
  
  // Cleanup all timers when component unmounts
  useEffect(() => {
    return () => {
      timerIds?.current?.forEach(timerId => clearTimeout(timerId));
    };
  }, []);

  // Register toast service for Redux middleware
  useEffect(() => {
    toastService.register(showToast);
    return () => {
      toastService.unregister();
    };
  }, [showToast]);



  try {
    return (
      <ToastContext.Provider value={{ showToast }}>
        {children}
        
        {/* Render all active toasts */}
        {toasts.map((toast, index) => (
          <Snackbar
            key={toast.id}
            open={true}
            autoHideDuration={toast.duration}
            onClose={() => removeToast(toast.id)}
            anchorOrigin={{ 
              vertical: 'bottom', 
              horizontal: 'center' 
            }}
            sx={{
              // Stack multiple toasts vertically from bottom
              bottom: styles.snackbar.bottom(index)
            }}
          >
            <Alert
              onClose={() => removeToast(toast.id)}
              variant="filled"
              icon={
                toast.customIcon === false ? false : toast.customIcon
              }
              sx={{
                ...styles.alert.base,
                ...styles.alert.withCustomColor(toast.customColor)
              }}
            >
              {toast.message}
            </Alert>
          </Snackbar>
        ))}
      </ToastContext.Provider>
    );
  } catch (error) {
    // If toast rendering fails, just render children without toasts
    console.error('ToastProvider render error:', error);
    return <>{children}</>;
  }
};
